import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { formatDateTime } from '../lib/format';
import { can, useSession } from '../lib/session';
import { useResyncPass, useRevokePass, useWalletPasses } from '../lib/queries';
import type { WalletPass } from '../lib/queries';
import {
  Alert, Button, Card, EmptyState, Field, Pill, SectionTitle, Select, Skeleton, Stat,
} from '../components/ui';

const TONE: Record<string, 'neutral' | 'positive' | 'warning' | 'danger'> = {
  NOT_ISSUED: 'neutral',
  ISSUED: 'positive',
  ACTIVE: 'positive',
  ERROR: 'danger',
  REVOKED: 'warning',
};

function PassRow({ pass, mayIssue, mayRevoke }: {
  pass: WalletPass; mayIssue: boolean; mayRevoke: boolean;
}) {
  const { t } = useI18n();
  const [revoking, setRevoking] = useState(false);
  const [reason, setReason] = useState('');
  const resync = useResyncPass();
  const revoke = useRevokePass();
  const errorText = useApiError();
  const error = revoke.error instanceof ApiError ? revoke.error : null;

  return (
    <div className="rounded-xl border border-line bg-raised px-3.5 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/customers/${pass.customerId}`}
              className="-my-2 inline-block truncate py-2 text-sm font-medium text-ink hover:text-brand"
            >
              {pass.customerName}
            </Link>
            <Pill>{pass.provider === 'APPLE' ? 'Apple' : 'Google'}</Pill>
            <Pill tone={TONE[pass.status] ?? 'neutral'}>{pass.status.toLowerCase().replace('_', ' ')}</Pill>
          </div>
          <div className="tabular mt-1 text-[0.68rem] text-ink-faint">
            {pass.loyaltyId}
            {pass.lastSyncedAt && ` · ${t('wallets.synced', { when: formatDateTime(pass.lastSyncedAt) })}`}
            {!pass.lastSyncedAt && pass.status === 'NOT_ISSUED' && ` · ${t('wallets.waitingToIssue')}`}
            {pass.version > 1 && ` · v${pass.version}`}
          </div>
          {pass.lastError && (
            <p className="mt-1.5 text-[0.7rem] leading-relaxed text-danger">{pass.lastError}</p>
          )}
        </div>

        {!pass.revokedAt && (
          <div className="flex shrink-0 gap-1.5">
            {mayIssue && (
              <Button
                variant="quiet"
                size="sm"
                loading={resync.isPending}
                onClick={() => resync.mutate(pass.id)}
              >
                {t('wallets.resync')}
              </Button>
            )}
            {mayRevoke && !revoking && (
              <Button variant="quiet" size="sm" onClick={() => setRevoking(true)}>{t('wallets.revoke')}</Button>
            )}
          </div>
        )}
      </div>

      {revoking && (
        <div className="mt-3 border-t border-line pt-3">
          {error && <div className="mb-2"><Alert>{errorText(error) ?? error.message}</Alert></div>}
          <Field
            label={t('common.reason')}
            placeholder={t('wallets.revokeReason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <p className="mt-1.5 text-[0.68rem] text-ink-faint">
            {t('wallets.revokeWarning')}
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              variant="danger"
              size="sm"
              disabled={reason.trim().length < 3}
              loading={revoke.isPending}
              onClick={() => revoke.mutate(
                { id: pass.id, reason: reason.trim() },
                { onSuccess: () => setRevoking(false) },
              )}
            >
              {t('wallets.revoke')}
            </Button>
            <Button variant="quiet" size="sm" onClick={() => setRevoking(false)}>{t('common.cancel')}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function WalletsPage() {
  const { t } = useI18n();
  const { data: session } = useSession();
  const [status, setStatus] = useState('');
  const { data, isPending } = useWalletPasses(status);

  const mayIssue = can(session, 'wallet.issue');
  const mayRevoke = can(session, 'wallet.revoke');
  const passes = data?.items ?? [];
  // Named, not lumped together: one provider can be live while the other is
  // still a stand-in, and "passes will not install" is then simply untrue.
  const mocked = [
    data?.providers.apple === 'mock' ? 'Apple Wallet' : null,
    data?.providers.google === 'mock' ? 'Google Wallet' : null,
  ].filter(Boolean).join(', ');

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="mb-1 font-display text-[2rem] font-bold tracking-tight text-ink">{t('wallets.title')}</h1>

      {mocked !== '' && (
        <div className="mb-4">
          <Alert tone="warning">
            {t('wallets.mockWarning', { providers: mocked })}
          </Alert>
        </div>
      )}

      {data && (
        <section className="mb-4 grid gap-3 grid-cols-2 lg:grid-cols-4">
          <Stat label={t('wallets.live')} value={data.totals.live} sub={t('wallets.liveSub')} />
          <Stat label={t('wallets.queued')} value={data.totals.notIssued} sub={t('wallets.queuedSub')} />
          <Stat label={t('wallets.failing')} value={data.totals.failing} sub={t('wallets.failingSub')} />
          <Stat label={t('wallets.revoked')} value={data.totals.revoked} sub={t('wallets.revokedSub')} />
        </section>
      )}

      <div className="mb-4 max-w-xs">
        <Select label={t('common.show')} value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="">{t('wallets.filterAll')}</option>
          <option value="NOT_ISSUED">{t('wallets.filterNotIssued')}</option>
          <option value="ISSUED">{t('wallets.filterIssued')}</option>
          <option value="ACTIVE">{t('wallets.filterActive')}</option>
          <option value="ERROR">{t('wallets.filterError')}</option>
          <option value="REVOKED">{t('wallets.filterRevoked')}</option>
        </Select>
      </div>

      {isPending && <Card><Skeleton className="h-24" /></Card>}

      {!isPending && passes.length === 0 && (
        <EmptyState
          title={status ? t('wallets.emptyFiltered') : t('wallets.empty')}
        />
      )}

      {passes.length > 0 && (
        <Card>
          <SectionTitle>{t('wallets.count', { count: passes.length })}</SectionTitle>
          <div className="space-y-2">
            {passes.map((pass) => (
              <PassRow key={pass.id} pass={pass} mayIssue={mayIssue} mayRevoke={mayRevoke} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
